import { signal } from "../net/signal";
import { httpUrl } from "../net/config";
import { games, type Game } from "./games";
import type { Hall } from "./hall";
import hallDefault from "./hall_default.json";

/**
 * در دسترس‌بودنِ بازی‌ها — جدا از خودِ کاتالوگ.
 *
 * `games.ts` را سازنده‌ی سایتِ ایستا با نودِ خالی و بی‌باندلر import می‌کند، پس
 * باید فقط داده باشد و هیچ ایمپورتِ مرورگری نداشته باشد. این‌جا کدِ زمانِ
 * اجراست و فقط اپِ وب می‌خواندش.
 */

/**
 * کدام بازی‌ها ادمین خاموششان کرده.
 *
 * فهرستِ بالا در بیلد ثابت است؛ این یکی از `/v1/live` می‌آید و همان لحظه عوض
 * می‌شود. سیگنال است و نه یک `await` سرِ راهِ رندر: لابی نباید منتظرِ شبکه
 * بماند — تا رسیدنِ پاسخ همه‌چیز روشن فرض می‌شود و اگر چیزی خاموش بود، یک
 * لحظه بعد از فهرست می‌رود. برعکسش بدتر بود: لابیِ خالی تا رسیدنِ شبکه.
 */
const offSite = signal<Set<string>>(new Set());
const offOnline = signal<Set<string>>(new Set());

/**
 * تالارِ بازی‌ها از پنل (GET /v1/hall?for=site) — قفسه‌های /play، نام و ترتیبشان. تا رسیدنِ پاسخ همان
 * پیش‌فرضِ داخلِ بسته (= seed ِ سرور)؛ مثلِ خاموشی‌ها، لابی منتظرِ شبکه نمی‌ماند.
 */
export const hall = signal<Hall>(hallDefault as unknown as Hall);

export async function loadHall(): Promise<void> {
  try {
    const r = await fetch(httpUrl("/v1/hall?for=site"), { cache: "no-store" });
    if (!r.ok) return;
    const j = (await r.json()) as Hall;
    if (Array.isArray(j.games) && Array.isArray(j.lists) && j.games.length > 0) hall.value = j;
  } catch {
    /* شبکه نبود — پیش‌فرض می‌مانَد */
  }
}

/** نامِ بازی در تالار (پنل) — نبود = نامِ کاتالوگِ سایت. */
export function hallName(h: Hall, g: Game): Game {
  const d = h.games.find((x) => x.id === g.id);
  return d && !d.hidden ? { ...g, fa: d.fa || g.fa, note: d.note || g.note, blurb: d.blurb || g.blurb } : g;
}

/**
 * بازی‌هایی که همین حالا روی قفسه‌اند.
 *
 * فقط خاموشیِ **سایت** بازی را از قفسه برمی‌دارد. خاموشیِ **آنلاین** یعنی
 * «بی چندنفره» و نه «بی بازی»: بازی می‌مانَد و انتخابش مستقیم میزی با ربات‌ها
 * می‌سازد. بی این، بازی‌ای که از آنلاین درمی‌آمد کلاً ناپدید می‌شد.
 */
export function liveGames(offS: Set<string>, _offO: Set<string>): Game[] {
  return games.filter((g) => !offS.has(g.id));
}

/** این بازی چندنفره دارد؟ */
export const hasMultiplayer = (offO: Set<string>, id: string) => !offO.has(id);

export { offSite, offOnline };

/**
 * یک‌بار سرِ بالا آمدن می‌پرسد و بس.
 *
 * خاموش‌کردنِ بازی کارِ نادری است و ارزشِ نظرسنجیِ هر چند ثانیه را ندارد؛ هر
 * تازه‌کردنِ صفحه تازه‌اش می‌کند. خطا بی‌صداست: نرسیدنِ این پاسخ نباید لابی را
 * از کار بیندازد.
 */
export async function loadAvailability(): Promise<void> {
  try {
    const r = await fetch(httpUrl("/v1/live"), { cache: "no-store" });
    if (!r.ok) return;
    const j = (await r.json()) as { games_off_site?: string[]; games_off_online?: string[] };
    offSite.value = new Set(j.games_off_site ?? []);
    offOnline.value = new Set(j.games_off_online ?? []);
  } catch {
    /* شبکه نبود — همه روشن می‌مانند */
  }
}
